import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import type { BrandFilterStateSet, BrandUuid } from '@vs-shop/schemas/product/filter/state/brand';
import { Context, Effect, HashMap, HashSet, Layer, Option, Stream, SubscriptionRef } from 'effect';

import { ContextTag } from '../context';

export type ContextualBrandFilterStateSet = HashMap.HashMap<ProductContextId, BrandFilterStateSet>;

export const emptyBrandSet = HashSet.empty<BrandUuid>();

export const createBrandFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualBrandFilterStateSet>,
  context: ProductContextId
) => {
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyBrandSet)),
    Stream.changes
  );
};

export const createIsBrandSelectedStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualBrandFilterStateSet>,
  context: ProductContextId,
  brandUuid: BrandUuid
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(
      Option.match({
        onNone: () => false,
        onSome: (brandSet) => HashSet.has(brandSet, brandUuid),
      })
    )
  );

export const createBrandHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualBrandFilterStateSet>,
  context: ProductContextId
) => createBrandFiltersStream(ref, context).pipe(Stream.map((brands) => HashSet.size(brands) > 0));

export class BrandFilterService extends Context.Service<BrandFilterService>()(
  ContextTag.BrandFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, BrandFilterStateSet>();

      const filtersRef =
        yield* SubscriptionRef.make<ContextualBrandFilterStateSet>(emptyContextMap);

      const getBrands = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyBrandSet));
        });

      const addBrand = (context: ProductContextId, brandUuid: BrandUuid) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentBrands = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyBrandSet)
          );
          const updatedBrands = HashSet.add(currentBrands, brandUuid);
          return HashMap.set(contextMap, context, updatedBrands);
        });

      const removeBrand = (context: ProductContextId, brandUuid: BrandUuid) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentBrands = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyBrandSet)
          );
          const updatedBrands = HashSet.remove(currentBrands, brandUuid);

          if (HashSet.size(updatedBrands) === 0) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedBrands);
        });

      const toggleBrand = (context: ProductContextId, brandUuid: BrandUuid) =>
        Effect.gen(function* () {
          const currentBrands = yield* getBrands(context);
          if (HashSet.has(currentBrands, brandUuid)) {
            yield* removeBrand(context, brandUuid);
          } else {
            yield* addBrand(context, brandUuid);
          }
        });

      const clearBrands = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setBrands = (context: ProductContextId, brands: BrandFilterStateSet) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, brands));

      const getBrandsStream = (context: ProductContextId) =>
        createBrandFiltersStream(filtersRef, context);

      const isBrandSelectedStream = (context: ProductContextId, brandUuid: BrandUuid) =>
        createIsBrandSelectedStream(filtersRef, context, brandUuid);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createBrandHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        getBrands,
        addBrand,
        removeBrand,
        toggleBrand,
        clearBrands,
        setBrands,
        getBrandsStream,
        isBrandSelectedStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
